import { RecordsData } from './../modals/patients/import-patients-modal/import-patients-modal.interface';
import { createAction, props } from '@ngrx/store';
import {
    AuditHistoryRecord,
    CampaignDetails,
    CampaignListSorting,
    DownloadAuditList,
    DownloadOptOutsParam,
} from '../model/campaign.interface';
import { CommunicationChannel, CommunicationHistoryRecord, CommunicationDetail, SmsCommunicationDetail } from '../model/campaign.interface';
import { UnsubscribeListSorting, UnsubscribeListType } from './campaign.state';
import {
    UnsubscribeRecord,
    recordsDataInterface,
} from '../model/unsubscribe.interface';

// ─── Campaign List Page Actions ───────────────────────────────────────────────

export const loadStandardCampaigns = createAction(
    '[Campaign List Page] Load Standard Campaigns',
    props<{ page: number }>(),
);

export const loadArchivedCampaigns = createAction(
    '[Campaign List Page] Load Archived Campaigns',
    props<{ page: number }>(),
);

export const setActiveTab = createAction(
    '[Campaign List Page] Set Active Tab',
    props<{ tab: number }>(),
);

export const setSearch = createAction(
    '[Campaign List Page] Set Search',
    props<{ search: string }>(),
);

export const setStandardSorting = createAction(
    '[Campaign List Page] Set Standard Sorting',
    props<{ sorting: CampaignListSorting }>(),
);

export const setArchivedSorting = createAction(
    '[Campaign List Page] Set Archived Sorting',
    props<{ sorting: CampaignListSorting }>(),
);

export const setStandardPage = createAction(
    '[Campaign List Page] Set Standard Page',
    props<{ page: number }>(),
);

export const setArchivedPage = createAction(
    '[Campaign List Page] Set Archived Page',
    props<{ page: number }>(),
);

export const setStandardStatusFilter = createAction(
    '[Campaign List Page] Set Standard Status Filter',
    props<{ statuses: string[] }>(),
);

export const setArchivedStatusFilter = createAction(
    '[Campaign List Page] Set Archived Status Filter',
    props<{ statuses: string[] }>(),
);

export const setStandardPageSize = createAction(
  '[Campaign List Page] Set Standard Page Size',
  props<{ pageSize: number }>()
);

export const setArchivedPageSize = createAction(
  '[Campaign List Page] Set Archived Page Size',
  props<{ pageSize: number }>()
);

// ─── Campaign List API Actions ────────────────────────────────────────────────

export const loadStandardCampaignsSuccess = createAction(
    '[Campaign List API] Load Standard Campaigns Success',
    props<{
        campaigns: any[];
        filteredCount: number;
        totalActiveCount: number;
        totalArchivedCount: number;
        page: number;
        enableSecureDriveImport: boolean;
    }>(),
);

export const loadStandardCampaignsFailure = createAction(
    '[Campaign List API] Load Standard Campaigns Failure',
    props<{ error: string }>(),
);

export const loadArchivedCampaignsSuccess = createAction(
    '[Campaign List API] Load Archived Campaigns Success',
    props<{
        campaigns: any[];
        totalActiveCount: number;
        totalArchivedCount: number;
        filteredCount: number;
        page: number;
    }>(),
);

export const loadArchivedCampaignsFailure = createAction(
    '[Campaign List API] Load Archived Campaigns Failure',
    props<{ error: string }>(),
);

// ─── Campaign Polling Actions ─────────────────────────────────────────────────

export const startPolling = createAction(
    '[Campaign Polling] Start Polling',
    props<{ ids: string }>(),
);

export const stopPolling = createAction('[Campaign Polling] Stop Polling');

export const pollSuccess = createAction(
    '[Campaign Polling] Poll Success',
    props<{ statuses: any[] }>(),
);

export const pollFailure = createAction(
    '[Campaign Polling] Poll Failure',
    props<{ error: string }>(),
);

// ─── Campaign Operations Actions ──────────────────────────────────────────────

export const copyCampaignSuccess = createAction(
    '[Campaign Operations] Copy Campaign Success',
);

export const archiveCampaignSuccess = createAction(
    '[Campaign Operations] Archive Campaign Success',
    props<{ adjustedPage: number }>(),
);

export const campaignStatusChanged = createAction(
    '[Campaign Operations] Campaign Status Changed',
);

// ─── Phase 2: Campaign Detail Actions ─────────────────────────────────────────

export const loadCampaignDetail = createAction(
    '[Campaign Detail Page] Load Campaign Detail',
    props<{ campaignId: string }>(),
);

export const loadCampaignDetailSuccess = createAction(
    '[Campaign Detail API] Load Campaign Detail Success',
    props<{ campaignDetails: CampaignDetails; campaignId: string }>(),
);

export const loadCampaignDetailFailure = createAction(
    '[Campaign Detail API] Load Campaign Detail Failure',
    props<{ error: string }>(),
);

export const initNewCampaign = createAction(
    '[Campaign Detail Page] Init New Campaign',
);

export const updateCampaignDetails = createAction(
    '[Campaign Detail Page] Update Campaign Details',
    props<{ campaignDetails: CampaignDetails }>(),
);

export const saveCampaign = createAction(
    '[Campaign Detail Page] Save Campaign',
    props<{ status: string }>(),
);

export const saveCampaignSuccess = createAction(
    '[Campaign Detail API] Save Campaign Success',
    props<{ campaignDetails: CampaignDetails; campaignId: string }>(),
);

export const saveCampaignFailure = createAction(
    '[Campaign Detail API] Save Campaign Failure',
    props<{ error: string }>(),
);

export const resetCampaignDetail = createAction(
    '[Campaign Detail Page] Reset Campaign Detail',
);
export const downloadAuditList = createAction(
    '[Campaign Detail Page] Download Audit List',
    props<{ query: DownloadAuditList }>(),
);

export const downloadAuditListSuccess = createAction(
    '[Campaign Detail Page] Download Audit List Success',
);

export const downloadAuditListFailure = createAction(
    '[Campaign Detail Page] Download Audit List Failure',
    props<{ error: string }>(),
);
// ─── Campaign Audit History Panel Actions ───────────────────────────────────────

export const loadAuditHistory = createAction(
    '[Campaign History Panel] Load Audit History',
    props<{ campaignId: string | null; pageNumber: number; limit: number }>(),
);

export const loadAuditHistorySuccess = createAction(
    '[Campaign History Panel] Load Audit History Success',
    props<{ records: AuditHistoryRecord[]; totalCount: number; pageNumber: number; limit: number }>(),
);

export const loadAuditHistoryFailure = createAction(
    '[Campaign History Panel] Load Audit History Failure',
    props<{ error: string }>(),
);

export const resetAuditHistory = createAction(
    '[Campaign History Panel] Reset Audit History',
);

// ─── Communication History Search Actions ───────────────────────────────────

export const searchCommunicationHistory = createAction(
    '[Communication History Search] Search',
    props<{ channel: CommunicationChannel; contact: string; campaignName?: string; listName?: string; sortOrder?: 'ASC' | 'DESC'; pageSize?: number }>(),
);

export const searchCommunicationHistorySuccess = createAction(
    '[Communication History Search] Search Success',
    props<{ records: CommunicationHistoryRecord[]; matchingRecordCount: number; nextCursor: string | null; isFirstPage: boolean }>(),
);

export const searchCommunicationHistoryFailure = createAction(
    '[Communication History Search] Search Failure',
    props<{ error: string }>(),
);

export const loadMoreCommunicationHistory = createAction(
    '[Communication History Search] Load More',
    props<{ pageSize?: number }>(),
);

export const selectCommunicationHistoryRecord = createAction(
    '[Communication History Search] Select Record',
    props<{ record: CommunicationHistoryRecord | null }>(),
);

export const clearCommunicationHistorySearch = createAction(
    '[Communication History Search] Clear',
);

export const loadCommunicationDetail = createAction(
    '[Communication History Detail] Load',
    props<{ campaignTaskId: string; channel: CommunicationChannel }>(),
);

export const loadCommunicationDetailSuccess = createAction(
    '[Communication History Detail] Load Success',
    props<{ detail: CommunicationDetail | SmsCommunicationDetail }>(),
);

export const loadCommunicationDetailFailure = createAction(
    '[Communication History Detail] Load Failure',
    props<{ error: string }>(),
);

// ─── Phase 3: Campaign Workflow Actions ───────────────────────────────────────

export const setWorkflowTabData = createAction(
    '[Campaign Workflow] Set Workflow Tab Data',
    props<{ workflowTabData: any }>(),
);

export const setCommunicationNodeData = createAction(
    '[Campaign Workflow] Set Communication Node Data',
    props<{ communicationNodeData: Record<string, any> }>(),
);

export const setActiveCommunicationId = createAction(
    '[Campaign Workflow] Set Active Communication Id',
    props<{ id: string | null }>(),
);

export const updateNodeLabel = createAction(
    '[Campaign Workflow] Update Node Label',
    props<{ stateId: string; label: string }>(),
);

export const triggerFitNetwork = createAction(
    '[Campaign Workflow] Trigger Fit Network',
);

export const cleanStaleNodes = createAction(
    '[Campaign Workflow] Clean Stale Nodes',
    props<{ activeNodeIds: string[] }>(),
);

export const updateNodeCommunicationData = createAction(
    '[Campaign Workflow] Update Node Communication Data',
    props<{ nodeId: string; data: any }>(),
);

// ─── Phase 4: Import Patient Actions ──────────────────────────────────────────

export const setFileExistOnRedis = createAction(
    '[Campaign Import] Set File Exist On Redis',
    props<{ exists: boolean }>(),
);

export const setFileId = createAction(
    '[Campaign Import] Set File Id',
    props<{ fileId: number | null }>(),
);

export const setIsEmailOrPhonePresent = createAction(
    '[Campaign Import] Set Is Email Or Phone Present',
    props<{ present: boolean }>(),
);

export const setIsFileValidating = createAction(
    '[Campaign Import] Set Is File Validating',
    props<{ validating: boolean }>(),
);

export const setIsDataMappingGridPopulated = createAction(
    '[Campaign Import] Set Is Data Mapping Grid Populated',
    props<{ populated: boolean }>(),
);

export const setRecordsData = createAction(
    '[Campaign Import] Set Records Data',
    props<{ recordsData: RecordsData }>(),
);

export const resetRecordsData = createAction(
    '[Campaign Import] Reset Records Data',
);

export const setImportUnsavedChanges = createAction(
    '[Campaign Import] Set Unsaved Changes',
    props<{ hasChanges: boolean }>(),
);

export const setFileUploadCompleted = createAction(
    '[Campaign Import] Set File Upload Completed',
    props<{ completed: boolean }>(),
);

export const resetImportState = createAction(
    '[Campaign Import] Reset Import State',
);

// ─── Phase 4: Unsubscribe Management Actions ────────────────────────────────

export const setUnsubscribeActiveTab = createAction(
    '[Unsubscribe Page] Set Active Tab',
    props<{ tab: UnsubscribeListType }>(),
);

export const setUnsubscribeSearch = createAction(
    '[Unsubscribe Page] Set Search',
    props<{ listType: UnsubscribeListType; search: string }>(),
);

export const setUnsubscribeDomain = createAction(
    '[Unsubscribe Page] Set Domain',
    props<{ domain: string | null }>(),
);

export const setUnsubscribeDateRange = createAction(
    '[Unsubscribe Page] Set Date Range',
    props<{
        listType: UnsubscribeListType;
        startDate: Date | null;
        endDate: Date | null;
    }>(),
);

export const clearUnsubscribeFilters = createAction(
    '[Unsubscribe Page] Clear Filters',
    props<{ listType: UnsubscribeListType }>(),
);

export const setUnsubscribeSorting = createAction(
    '[Unsubscribe Page] Set Sorting',
    props<{ listType: UnsubscribeListType; sorting: UnsubscribeListSorting }>(),
);

export const setUnsubscribePage = createAction(
    '[Unsubscribe Page] Set Page',
    props<{ listType: UnsubscribeListType; page: number }>(),
);

export const loadUnsubscribeList = createAction(
    '[Unsubscribe Page] Load List',
    props<{ listType: UnsubscribeListType; page: number }>(),
);

export const loadUnsubscribeListSuccess = createAction(
    '[Unsubscribe API] Load List Success',
    props<{
        listType: UnsubscribeListType;
        records: UnsubscribeRecord[];
        totalCount: number;
        page: number;
    }>(),
);

export const loadUnsubscribeListFailure = createAction(
    '[Unsubscribe API] Load List Failure',
    props<{ error: string }>(),
);

export const loadUnsubscribeDomains = createAction(
    '[Unsubscribe Page] Load Domains',
);

export const loadUnsubscribeDomainsSuccess = createAction(
    '[Unsubscribe API] Load Domains Success',
    props<{ domains: string[] }>(),
);

export const loadUnsubscribeDomainsFailure = createAction(
    '[Unsubscribe API] Load Domains Failure',
    props<{ error: string }>(),
);

export const loadUnsubscribeActivePhone = createAction(
    '[Unsubscribe Page] Load Active Phone',
);

export const loadUnsubscribeActivePhoneSuccess = createAction(
    '[Unsubscribe API] Load Active Phone Success',
    props<{ phoneNumber: string }>(),
);

export const loadUnsubscribeActivePhoneFailure = createAction(
    '[Unsubscribe API] Load Active Phone Failure',
    props<{ error: string }>(),
);

export const resubscribeContact = createAction(
    '[Unsubscribe Page] Resubscribe Contact',
    props<{ listType: UnsubscribeListType; id: string }>(),
);

export const resubscribeContactSuccess = createAction(
    '[Unsubscribe API] Resubscribe Contact Success',
    props<{ listType: UnsubscribeListType; message: string }>(),
);

export const resubscribeContactFailure = createAction(
    '[Unsubscribe API] Resubscribe Contact Failure',
    props<{ error: string }>(),
);

export const exportUnsubscribeList = createAction(
    '[Unsubscribe Page] Export List',
    props<{ query: DownloadOptOutsParam }>(),
);

export const exportUnsubscribeListSuccess = createAction(
    '[Unsubscribe API] Export List Success',
);

export const exportUnsubscribeListFailure = createAction(
    '[Unsubscribe API] Export List Failure',
    props<{ error: string }>(),
);

export const resetUnsubscribeState = createAction(
    '[Unsubscribe Page] Reset All State',
);

// ─── Phase 5: Unsubscribe Import Actions ────────────────────────────────────

export const setUsubImportContext = createAction(
    '[Unsub Import] Set Context',
    props<{
        listType: UnsubscribeListType;
        mode: 'manual' | 'import' | null;
    }>(),
);

export const setUsubImportSelectedDomain = createAction(
    '[Unsub Import] Set Selected Domain',
    props<{ selectedDomain: string | null }>(),
);

export const setUsubImportManualEntries = createAction(
    '[Unsub Import] Set Manual Entries',
    props<{ emails: string[]; phones: string[] }>(),
);

export const setUsubImportRequiredColumnMapped = createAction(
    '[Unsub Import] Set Required Column Mapped',
    props<{ isPresent: boolean }>(),
);

export const setUsubImportUnsavedChanges = createAction(
    '[Unsub Import] Set Unsaved Changes',
    props<{ hasUnsavedChanges: boolean }>(),
);

export const resetUsubImportStasubImportState = createAction(
    '[Unsub Import] Reset State',
);

export const submitUsubManualOptOuts = createAction(
    '[Unsub Import] Submit Manual Opt Outs',
    props<{
        payload: {
            phoneNumbers?: string[];
            emails?: string[];
            domain?: string;
            type: string;
        };
        listType: UnsubscribeListType;
    }>(),
);

export const submitUsubManualOptOutsSuccess = createAction(
    '[Unsub Import API] Submit Manual Opt Outs Success',
    props<{
        message: string;
        listType: UnsubscribeListType;
        actualValidRows: number | null;
    }>(),
);

export const submitUsubManualOptOutsFailure = createAction(
    '[Unsub Import API] Submit Manual Opt Outs Failure',
    props<{ error: string }>(),
);

export const startUsubFileValidation = createAction(
    '[Unsub Import] Start File Validation',
    props<{
        listType: UnsubscribeListType;
        file: File;
        mappings: Record<string, string>;
        commType: 'email' | 'sms';
    }>(),
);

export const startUsubFileValidationSuccess = createAction(
    '[Unsub Import API] Start File Validation Success',
    props<{ fileId: number; fileNameWithId: string; fileName: string }>(),
);

export const startUsubFileValidationFailure = createAction(
    '[Unsub Import API] Start File Validation Failure',
    props<{ error: string }>(),
);

export const requestUsubSasUrl = createAction(
    '[Unsub Import] Request SAS URL',
    props<{ file: File; fileNameWithId: string }>(),
);

export const requestUsubSasUrlSuccess = createAction(
    '[Unsub Import API] Request SAS URL Success',
    props<{
        file: File;
        fileNameWithId: string;
        sasUrl: string;
        containerName: string;
        sasToken: string;
    }>(),
);

export const requestUsubSasUrlFailure = createAction(
    '[Unsub Import API] Request SAS URL Failure',
    props<{ error: string }>(),
);

export const startUsubBlobUpload = createAction(
    '[Unsub Import] Start Blob Upload',
    props<{
        file: File;
        fileNameWithId: string;
        sasUrl: string;
        containerName: string;
        sasToken: string;
    }>(),
);

export const startUsubBlobUploadSuccess = createAction(
    '[Unsub Import API] Start Blob Upload Success',
    props<{ fileNameWithId: string }>(),
);

export const startUsubBlobUploadFailure = createAction(
    '[Unsub Import API] Start Blob Upload Failure',
    props<{ error: string }>(),
);

export const startUsubHeartbeat = createAction(
    '[Unsub Import] Start Heartbeat',
    props<{ fileNameWithId: string }>(),
);

export const stopUsubHeartbeat = createAction('[Unsub Import] Stop Heartbeat');

export const startUsubValidationSse = createAction(
    '[Unsub Import] Start Validation SSE',
    props<{ fileNameWithId: string }>(),
);

export const usubValidationSseStatus = createAction(
    '[Unsub Import SSE] Validation Status',
    props<{ status: string }>(),
);

export const usubValidationSseCompleted = createAction(
    '[Unsub Import SSE] Validation Completed',
);

export const usubValidationSseFailed = createAction(
    '[Unsub Import SSE] Validation Failed',
    props<{ error: string }>(),
);

export const loadUsubImportSummary = createAction(
    '[Unsub Import] Load Summary',
    props<{ fileId: number }>(),
);

export const loadUsubImportSummarySuccess = createAction(
    '[Unsub Import API] Load Summary Success',
    props<{ recordsData: recordsDataInterface }>(),
);

export const loadUsubImportSummaryFailure = createAction(
    '[Unsub Import API] Load Summary Failure',
    props<{ error: string }>(),
);

export const confirmUsubFileImport = createAction(
    '[Unsub Import] Confirm File Import',
    props<{ fileName: string; listType: UnsubscribeListType }>(),
);

export const confirmUsubFileImportSuccess = createAction(
    '[Unsub Import API] Confirm File Import Success',
    props<{ fileName: string; listType: UnsubscribeListType }>(),
);

export const confirmUsubFileImportFailure = createAction(
    '[Unsub Import API] Confirm File Import Failure',
    props<{ error: string }>(),
);

export const startUsubUploadSse = createAction(
    '[Unsub Import] Start Upload SSE',
    props<{ fileName: string; listType: UnsubscribeListType }>(),
);

export const usubUploadSseStatus = createAction(
    '[Unsub Import SSE] Upload Status',
    props<{ status: string }>(),
);

export const usubUploadSseCompleted = createAction(
    '[Unsub Import SSE] Upload Completed',
    props<{ listType: UnsubscribeListType; actualValidRows: number | null }>(),
);

export const usubUploadSseFailed = createAction(
    '[Unsub Import SSE] Upload Failed',
    props<{ error: string }>(),
);

export const usubImportCompleted = createAction(
    '[Unsub Import] Completed',
    props<{
        listType: UnsubscribeListType;
        message: string;
        actualValidRows: number | null;
    }>(),
);

export const resetUsubImportState = createAction('[Unsub Import] Reset State');
